import * as vp from 'vopay';
import * as Yup from 'yup';
import { FunctionComponent } from 'react';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useLayout } from '../../../../../providers/Layout.provider';
import styles from './PaymentMethod.module.scss';
import { usePaylink } from '../../../../../providers/Paylink.provider';

const { Box, TextField, Button, ErrorText } = vp;

type PaymentMethodFormProps = {
  defaultValues: {
    Amount: string;
    MaximumAmount: string;
  };
  onSave: (values: { Amount: string; MaximumAmount: string }) => void;
  onCancel: () => void;
};

type FC = FunctionComponent<PaymentMethodFormProps>;

const PaymentMethodForm: FC = (props: PaymentMethodFormProps) => {
  const { color } = useLayout();
  const { paylink } = usePaylink();
  const { defaultValues, onSave, onCancel } = props;

  const { t } = useTranslation('translations', { keyPrefix: 'MultiplePaymentMethods' });
  return (
    <Formik
      initialValues={defaultValues}
      enableReinitialize
      validationSchema={createdValidationSchema()}
      onSubmit={onSave}
    >
      {({ touched, errors, handleSubmit, values, isValid, getFieldProps }) => (
        <form onSubmit={handleSubmit}>
          <Box flx="col" base="8" gap="lmd">
            <Box flx="col" base="8" gap="sm">
              <TextField
                variant="lined"
                preset="currency"
                {...getFieldProps('Amount')}
                elementLeft={
                  <div
                    className={styles.InputText}
                    style={{
                      paddingLeft: '16px',
                      display: 'inline-block',
                      width: '47px',
                      position: 'relative',
                      marginRight: '-11px',
                    }}
                  >
                    {paylink?.Currency}
                  </div>
                }
                padding="0 19px 3px 19px"
                inputWidth="calc(100% - 187px)"
                elementRight={
                  <div
                    className={styles.InputText}
                    style={{ paddingRight: '16px', display: 'inline-block', width: '150px', textAlign: 'right' }}
                  >
                    {paylink?.Currency}{' '}
                    {Number(vp.sanitizeToNumeric(values.MaximumAmount)) - Number(vp.sanitizeToNumeric(values.Amount)) >=
                    0
                      ? vp.formattedCurrency(
                          (
                            Number(vp.sanitizeToNumeric(values.MaximumAmount)) -
                            Number(vp.sanitizeToNumeric(values.Amount))
                          ).toString(),
                        )
                      : vp.formattedCurrency(vp.sanitizeToNumeric(values.MaximumAmount) || '')}{' '}
                    Left
                  </div>
                }
                borderColor={errors.Amount && (touched.Amount || !!values.Amount) ? '#E24C4C' : '#C4C4C4'}
              />
              {errors.Amount && (touched.Amount || !!values.Amount) && (
                <ErrorText
                  size="md"
                  error={errors.Amount && (touched.Amount || !!values.Amount) ? errors.Amount : null}
                  fitContent
                />
              )}
            </Box>
            <Box height="auto" jc="fe" gap="sm" base="8" flx="row">
              <Button
                id="credit-card-connection-btn"
                label={`${t('cancel')}`}
                type="button"
                color="secondary"
                onClick={onCancel}
                height="32px"
                round
                bold
                noShadow
              />
              <Button
                id="credit-card-connection-btn"
                label={`${t('update')}`}
                type="submit"
                opacity={isValid ? '1' : '0.5'}
                background={color}
                isDisabled={!isValid}
                textColor={vp.getContrastColor(color)}
                height="32px"
                round
                bold
                noShadow
              />
            </Box>
          </Box>
        </form>
      )}
    </Formik>
  );

  function createdValidationSchema() {
    return Yup.object().shape({
      Amount: Yup.number()
        .required(`${t('requiredAmountError')}`)
        .transform((_value, originalValue: string) => Number(vp.sanitizeToNumeric(originalValue)))
        .max(
          Number(defaultValues.MaximumAmount),
          `${t('exceedsMaximumAmountError', { max: Number(defaultValues.MaximumAmount) })}`,
        )
        .min(0.01, `${t('minimumAmountError')}`),
    });
  }
};

export default PaymentMethodForm;
